import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { verifyPayment } from '../../services/api';

const PaymentSuccess = () => {
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('order_id');
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
    const [message, setMessage] = useState('');

    useEffect(() => {
        const verify = async () => {
            if (!orderId) {
                setStatus('error');
                setMessage('Missing order ID. Unable to verify payment.');
                setLoading(false);
                return;
            }

            try {
                // Call verification endpoint
                const result = await verifyPayment(orderId);
                if (result.success) {
                    setStatus('success');
                    setMessage(result.message || 'Your payment was successfully verified!');
                } else {
                    setStatus('error');
                    setMessage(result.message || 'Payment verification failed.');
                }
            } catch (error) {
                console.error('Verification error:', error);
                setStatus('error');
                setMessage(error.message || 'An error occurred during payment verification. Please contact support.');
            } finally {
                setLoading(false);
            }
        };

        verify();
    }, [orderId]);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden p-8 border border-gray-100 text-center transform transition-all duration-300">
                {status === 'verifying' && (
                    <div className="space-y-6 py-6">
                        <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-emerald-600"></div>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800">Verifying Payment</h2>
                        <p className="text-gray-500 max-w-sm mx-auto">
                            Please do not refresh the page or click back. We are verifying your transaction with Cashfree...
                        </p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="space-y-6 py-4">
                        <div className="flex justify-center">
                            <div className="h-20 w-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-4xl shadow-inner animate-bounce">
                                <i className="fas fa-check-circle text-green-600"></i>
                            </div>
                        </div>
                        <h2 className="text-3xl font-extrabold text-gray-900">Payment Successful!</h2>
                        <p className="text-emerald-700 font-semibold bg-emerald-50 py-2 px-4 rounded-full inline-block">
                            Course Unlocked ✓
                        </p>
                        <div className="border-t border-b border-gray-100 py-4 my-2 text-left space-y-3 text-sm text-gray-600">
                            <div className="flex justify-between">
                                <span>Order ID:</span>
                                <span className="font-mono font-medium text-gray-800">{orderId}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Status:</span>
                                <span className="text-green-600 font-bold">COMPLETED</span>
                            </div>
                        </div>
                        <p className="text-gray-500">
                            Congratulations! Your enrollment is complete. You can now access all lectures, materials, and certificates for this course.
                        </p>
                        <div className="pt-4">
                            <Link
                                to="/student/mycourses"
                                className="w-full inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors shadow-md hover:shadow-lg focus:outline-none"
                            >
                                <i className="fas fa-play mr-2"></i> Start Learning
                            </Link>
                        </div>
                    </div>
                )}

                {status === 'error' && (
                    <div className="space-y-6 py-4">
                        <div className="flex justify-center">
                            <div className="h-20 w-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-4xl shadow-inner">
                                <i className="fas fa-times-circle text-red-600"></i>
                            </div>
                        </div>
                        <h2 className="text-3xl font-extrabold text-red-600">Payment Failed</h2>
                        <div className="bg-red-50 text-red-800 p-4 rounded-xl text-sm border border-red-100">
                            {message}
                        </div>
                        <p className="text-gray-500">
                            If money was deducted from your account, please do not worry. It will be refunded, or you can contact support with your Order ID for manual activation.
                        </p>
                        <div className="pt-4 space-y-3">
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full inline-flex items-center justify-center bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 px-6 rounded-xl transition-colors focus:outline-none shadow-sm"
                            >
                                <i className="fas fa-sync mr-2"></i> Retry Verification
                            </button>
                            <Link
                                to="/student/support"
                                className="w-full inline-flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-colors focus:outline-none"
                            >
                                <i className="fas fa-headset mr-2"></i> Contact Support
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccess;
